import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Link } from "react-router-dom";
import { ArrowLeft, Warning, Users } from "@phosphor-icons/react";

export default function BDEWorkloadReport({ user, logout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/bde-workload?requester_role=${user?.role || ""}`);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, [user?.role]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = data?.rows || [];
  const maxTotal = Math.max(...rows.map(r => r.total_active), 1);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="px-8 pt-8 pb-6 max-w-6xl">
          <Link to="/reports-hub" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-hub">
            <ArrowLeft size={14} /> Reports Hub
          </Link>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-testid="bde-workload-title">
            BDE Workload Distribution
          </h1>
          <p className="text-slate-600 text-sm mt-1">Active leads + deals + quotes per BDE. Auto-flag overload (&gt;2× team avg).</p>
        </div>

        <div className="px-8 pb-12 max-w-6xl space-y-6">
          {loading ? (
            <div className="text-center text-slate-400 py-16">Loading…</div>
          ) : data?.error ? (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-6">{data.error}</div>
          ) : (
            <>
              {/* KPI tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="workload-kpis">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Team Size</div>
                  <div className="text-3xl font-black text-slate-900 mt-1 flex items-center gap-2">
                    <Users size={22} className="text-slate-400" /> {data?.team_size || 0}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Avg Active / BDE</div>
                  <div className="text-3xl font-black text-slate-900 mt-1">{data?.team_avg_active || 0}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Overload Threshold</div>
                  <div className="text-3xl font-black text-slate-900 mt-1">{data?.overload_threshold || 0}</div>
                </div>
                <div className={`border rounded-xl p-4 ${data?.overloaded_count > 0 ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    {data?.overloaded_count > 0 && <Warning size={12} className="text-amber-600" weight="fill" />} Overloaded
                  </div>
                  <div className={`text-3xl font-black mt-1 ${data?.overloaded_count > 0 ? "text-amber-700" : "text-slate-900"}`}>
                    {data?.overloaded_count || 0}
                  </div>
                </div>
              </div>

              {/* Workload table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="workload-table">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">BDE</th>
                      <th className="px-5 py-3 font-semibold text-right">Leads</th>
                      <th className="px-5 py-3 font-semibold text-right">Deals</th>
                      <th className="px-5 py-3 font-semibold text-right">Quotes</th>
                      <th className="px-5 py-3 font-semibold text-right">Total</th>
                      <th className="px-5 py-3 font-semibold">Distribution</th>
                      <th className="px-5 py-3 font-semibold text-right">vs Avg</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">No BDE accounts found.</td></tr>
                    ) : rows.map(r => (
                      <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`workload-row-${r.user_id}`}>
                        <td className="px-5 py-3">
                          <div className="font-semibold text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-500">{r.email}</div>
                        </td>
                        <td className="px-5 py-3 text-right text-slate-700">{r.active_leads}</td>
                        <td className="px-5 py-3 text-right text-slate-700">{r.active_deals}</td>
                        <td className="px-5 py-3 text-right text-slate-700">{r.active_quotes}</td>
                        <td className="px-5 py-3 text-right font-bold text-slate-900">{r.total_active}</td>
                        <td className="px-5 py-3 min-w-[140px]">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${r.fairness_flag === "overload" ? "bg-amber-500" : "bg-indigo-500"}`}
                                 style={{ width: `${(r.total_active / maxTotal) * 100}%` }} />
                          </div>
                        </td>
                        <td className={`px-5 py-3 text-right text-sm font-semibold ${r.vs_team_avg_pct > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                          {r.vs_team_avg_pct > 0 ? "+" : ""}{r.vs_team_avg_pct}%
                        </td>
                        <td className="px-5 py-3">
                          {r.fairness_flag === "overload" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                              <Warning size={11} weight="fill" /> Overload
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
